/**
 * Convert number to words (Indian numbering system)
 * Supports up to 99 crores (999,999,999)
 */

const ones = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen'
];

const tens = [
  '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'
];

const scales = ['', 'thousand', 'lakh', 'crore'];

function convertHundreds(num: number): string {
  let result = '';
  
  if (num >= 100) {
    result += ones[Math.floor(num / 100)] + ' hundred ';
    num %= 100;
  }
  
  if (num >= 20) {
    result += tens[Math.floor(num / 10)] + ' ';
    num %= 10;
  }
  
  if (num > 0) {
    result += ones[num] + ' ';
  }
  
  return result.trim();
}

function convertGroup(num: number, scale: string): string {
  if (num === 0) return '';
  return convertHundreds(num) + ' ' + scale + ' ';
}

export function numberToWords(num: number | string): string {
  // Handle edge cases
  if (num === 0 || num === '0') return 'zero only';
  if (!num || isNaN(Number(num))) return '';
  
  const numStr = num.toString();
  const numValue = parseFloat(numStr);
  
  if (numValue === 0) return 'zero only';
  if (numValue < 0) return 'negative ' + numberToWords(Math.abs(numValue));
  
  // Handle decimal part
  const parts = numStr.split('.');
  const integerPart = parseInt(parts[0], 10);
  const decimalPart = parts[1] ? parseInt(parts[1], 10) : 0;
  
  if (integerPart === 0 && decimalPart === 0) return 'zero only';
  
  let result = '';
  
  // Convert integer part using Indian numbering system
  if (integerPart > 0) {
    const numStr = integerPart.toString().padStart(9, '0');
    
    // Crores (7-8 digits)
    const crores = parseInt(numStr.substring(0, 2), 10);
    if (crores > 0) {
      result += convertGroup(crores, 'crore');
    }
    
    // Lakhs (5-6 digits)
    const lakhs = parseInt(numStr.substring(2, 4), 10);
    if (lakhs > 0) {
      result += convertGroup(lakhs, 'lakh');
    }
    
    // Thousands (3-4 digits)
    const thousands = parseInt(numStr.substring(4, 6), 10);
    if (thousands > 0) {
      result += convertGroup(thousands, 'thousand');
    }
    
    // Hundreds, Tens, Ones (1-2 digits)
    const remainder = parseInt(numStr.substring(6, 9), 10);
    if (remainder > 0) {
      result += convertHundreds(remainder) + ' ';
    }
  }
  
  // Handle decimal part (paise)
  if (decimalPart > 0) {
    const paise = convertHundreds(decimalPart);
    if (paise) {
      result += 'and ' + paise + ' paise ';
    }
  }
  
  // Clean up and add "only"
  result = result.trim().replace(/\s+/g, ' ');
  return result ? result + ' only' : 'zero only';
}

/**
 * Format number to words with currency
 */
export function numberToWordsWithCurrency(num: number | string, currency: string = 'rupees'): string {
  const words = numberToWords(num);
  if (words === 'zero only' || !words) return words;
  
  // Replace "only" with currency + "only"
  return words.replace(' only', ` ${currency} only`);
}
